import { supabase } from './supabase.js';
import { compressImageWithOptions } from './storageService.js';

export const AD_BANNERS_BUCKET = 'ad_banners';

const INTERNAL_ADVERTISER_EMAIL = 'publicidad-interna@dreamvinyl.es';
const INTERNAL_COMPANY = 'DreamVinyl (interno)';

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_VIDEO_BYTES = 28 * 1024 * 1024;

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const GIF_TYPES = new Set(['image/gif']);
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm']);

function safeFileName(name) {
  return String(name || 'banner')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .slice(0, 80);
}

/**
 * Anunciante interno para campañas creadas desde Admin.
 * @returns {Promise<string>} advertiser id
 */
export async function getOrCreateInternalAdvertiser() {
  const { data: existing, error: selErr } = await supabase
    .from('advertisers')
    .select('id')
    .eq('contact_email', INTERNAL_ADVERTISER_EMAIL)
    .maybeSingle();
  if (selErr) throw new Error(selErr.message || 'No se pudo buscar el anunciante interno');
  if (existing?.id) return existing.id;

  const { data: ins, error: insErr } = await supabase
    .from('advertisers')
    .insert({
      company_name: INTERNAL_COMPANY,
      contact_name: 'Administración DreamVinyl',
      contact_email: INTERNAL_ADVERTISER_EMAIL,
      country: 'ES',
      sector: 'otro',
      notes: 'Creado automáticamente para campañas gestionadas en panel Admin.',
    })
    .select('id')
    .single();
  if (insErr || !ins?.id) throw new Error(insErr?.message || 'No se pudo crear el anunciante interno');
  return ins.id;
}

/**
 * @param {File} file
 * @param {'image'|'gif'|'video'} mediaType
 */
export function validateAdMediaFile(file, mediaType) {
  if (!file) return 'Selecciona un archivo de imagen o vídeo.';
  const mt = String(mediaType || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  if (mt === 'video') {
    if (!VIDEO_TYPES.has(type)) return 'El vídeo debe ser MP4 o WebM.';
    if (file.size > MAX_VIDEO_BYTES) return 'El vídeo supera el máximo de 28 MB.';
    return null;
  }
  if (mt === 'gif') {
    if (!GIF_TYPES.has(type)) return 'Para GIF animado sube un archivo .gif.';
    if (file.size > MAX_IMAGE_BYTES) return 'El GIF supera el máximo de 8 MB.';
    return null;
  }
  if (!IMAGE_TYPES.has(type) && !GIF_TYPES.has(type)) {
    return 'La imagen debe ser JPG, PNG o WebP.';
  }
  if (file.size > MAX_IMAGE_BYTES) return 'La imagen supera el máximo de 8 MB.';
  return null;
}

/**
 * @param {File} file
 * @param {string} adId
 * @param {'image'|'gif'|'video'} mediaType
 * @returns {Promise<string>} URL pública
 */
export async function uploadAdBannerMedia(file, adId, mediaType) {
  const id = String(adId || '').trim();
  if (!id) throw new Error('Falta id de campaña para subir el archivo');
  const errMsg = validateAdMediaFile(file, mediaType);
  if (errMsg) throw new Error(errMsg);

  const mt = String(mediaType || '').toLowerCase();
  let payload = file;
  let contentType = file.type || 'application/octet-stream';
  let ext = (file.name.split('.').pop() || 'bin').toLowerCase();

  if (mt === 'image' && IMAGE_TYPES.has(contentType)) {
    payload = await compressImageWithOptions(file, { maxPx: 1600, quality: 0.85 });
    contentType = payload.type || 'image/webp';
    ext = 'webp';
  }

  const objectPath = `${id}/${Date.now()}-${safeFileName(file.name).replace(/\.[^.]+$/, '')}.${ext}`;
  const { error: upErr } = await supabase.storage.from(AD_BANNERS_BUCKET).upload(objectPath, payload, {
    upsert: true,
    contentType,
    cacheControl: '3600',
  });
  if (upErr) throw new Error(upErr.message || 'Error al subir el archivo');

  const { data: pub } = supabase.storage.from(AD_BANNERS_BUCKET).getPublicUrl(objectPath);
  const url = pub?.publicUrl;
  if (!url) throw new Error('No se obtuvo URL pública del banner');
  return url;
}

/**
 * @param {object} payload
 * @param {string} [existingAdId]
 */
export async function saveAdminAdCampaign(payload, existingAdId = null) {
  const advertiserId = await getOrCreateInternalAdvertiser();
  const row = {
    advertiser_id: advertiserId,
    campaign_name: String(payload.campaignName || '').trim().slice(0, 200),
    campaign_type: payload.campaignType === 'hero' ? 'hero' : 'display',
    slot: String(payload.slot || '').trim(),
    media_url: String(payload.mediaUrl || '').trim(),
    media_type: String(payload.mediaType || 'image').toLowerCase(),
    target_url: String(payload.targetUrl || 'https://dreamvinyl.es').trim().slice(0, 2048),
    alt_text: payload.altText ? String(payload.altText).trim().slice(0, 500) : null,
    starts_at: payload.startsAt,
    ends_at: payload.endsAt,
    status: String(payload.status || 'borrador').toLowerCase(),
    notes: payload.notes ? String(payload.notes).trim().slice(0, 2000) : null,
    sort_order: Number.isFinite(Number(payload.sortOrder)) ? Math.round(Number(payload.sortOrder)) : 0,
  };

  if (!row.campaign_name) throw new Error('Indica un nombre de campaña');
  if (!row.media_url) throw new Error('Sube una imagen o vídeo');
  if (!row.starts_at || !row.ends_at) throw new Error('Indica fecha de inicio y fin');
  if (new Date(row.ends_at) <= new Date(row.starts_at)) {
    throw new Error('La fecha de fin debe ser posterior al inicio');
  }

  const existingId = existingAdId ? String(existingAdId).trim() : '';
  if (existingId) {
    const { data, error } = await supabase.from('ads').update(row).eq('id', existingId).select('id').single();
    if (error) throw new Error(error.message || 'No se pudo actualizar la campaña');
    return data.id;
  }

  const { data, error } = await supabase.from('ads').insert(row).select('id').single();
  if (error) throw new Error(error.message || 'No se pudo crear la campaña');
  return data.id;
}

/**
 * @param {string} adId
 */
export async function fetchAdminAdForEdit(adId) {
  const id = String(adId || '').trim();
  if (!id) return null;
  const { data, error } = await supabase
    .from('ads')
    .select(
      'id, campaign_name, campaign_type, slot, media_url, media_type, target_url, alt_text, starts_at, ends_at, status, notes, sort_order'
    )
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message || 'No se pudo cargar la campaña');
  return data;
}

/**
 * Elimina campaña (solo borrador / rechazado / finalizado por RLS).
 * @param {string} adId
 */
export async function deleteAdminAd(adId) {
  const id = String(adId ?? '').trim();
  if (!id) throw new Error('Id no válido');
  const { error } = await supabase.from('ads').delete().eq('id', id);
  if (error) throw new Error(error.message || 'No se pudo eliminar');
}

/**
 * Duplica como borrador (misma creatividad y slot).
 * @param {string} adId
 * @returns {Promise<string>} nuevo id
 */
export async function duplicateAdminAd(adId) {
  const row = await fetchAdminAdForEdit(adId);
  if (!row) throw new Error('Campaña no encontrada');
  const name = `${row.campaign_name || 'Campaña'} (copia)`.slice(0, 200);
  return saveAdminAdCampaign(
    {
      campaignName: name,
      slot: row.slot,
      mediaType: row.media_type,
      mediaUrl: row.media_url,
      targetUrl: row.target_url,
      altText: row.alt_text,
      startsAt: row.starts_at,
      endsAt: row.ends_at,
      status: 'borrador',
      notes: row.notes,
      sortOrder: (row.sort_order ?? 0) + 1,
      campaignType: row.campaign_type,
    },
    null
  );
}

/**
 * @param {string} adId
 * @param {number} sortOrder
 */
export async function updateAdminAdSortOrder(adId, sortOrder) {
  const id = String(adId ?? '').trim();
  if (!id) throw new Error('Id no válido');
  const n = Math.round(Number(sortOrder));
  const { error } = await supabase.from('ads').update({ sort_order: n }).eq('id', id);
  if (error) throw new Error(error.message || 'No se pudo cambiar el orden');
}

/**
 * Conteo de campañas activas en fecha por slot (panel admin).
 * @returns {Promise<Record<string, number>>}
 */
export async function fetchActiveAdCountsBySlot() {
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from('ads')
    .select('slot')
    .eq('status', 'activo')
    .lte('starts_at', nowIso)
    .gte('ends_at', nowIso);
  if (error) throw new Error(error.message || 'No se pudieron contar campañas');
  const counts = {};
  for (const row of data ?? []) {
    const s = String(row.slot || '').trim();
    if (s) counts[s] = (counts[s] || 0) + 1;
  }
  return counts;
}
